import type { KeyboardEvent as ReactKeyboardEvent, ReactElement } from 'react'
import { useRef } from 'react'
import { cn } from '@/lib/cn'

export interface SegmentedControlOption {
  value: string
  label: string
  /** Rendered as a subtle pill after the label. */
  count?: number
}

export interface SegmentedControlProps {
  value: string
  onChange: (value: string) => void
  options: Array<SegmentedControlOption>
  /** Accessible name for the radiogroup. */
  label?: string
  className?: string
}

export function SegmentedControl({
  value,
  onChange,
  options,
  label,
  className
}: SegmentedControlProps): ReactElement {
  const buttons = useRef<Array<HTMLButtonElement | null>>([])
  const selectedIndex = options.findIndex((option) => option.value === value)
  // With no match, the first option stays reachable by Tab.
  const tabbableIndex = selectedIndex === -1 ? 0 : selectedIndex

  const moveTo = (index: number): void => {
    const option = options[index]
    if (!option) return
    onChange(option.value)
    buttons.current[index]?.focus()
  }

  const onKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>, index: number): void => {
    const count = options.length
    if (count === 0) return
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault()
        moveTo((index + 1) % count)
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault()
        moveTo((index - 1 + count) % count)
        break
      case 'Home':
        event.preventDefault()
        moveTo(0)
        break
      case 'End':
        event.preventDefault()
        moveTo(count - 1)
        break
      default:
        break
    }
  }

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        'inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-control border border-line bg-sunken p-1',
        className
      )}
    >
      {options.map((option, index) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            ref={(node) => {
              buttons.current[index] = node
            }}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={index === tabbableIndex ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[0.4375rem] px-2.5 py-1.5 text-xs font-semibold transition-colors duration-150 ease-out-quart',
              active
                ? 'border border-line bg-surface text-fg shadow-sheet'
                : 'border border-transparent text-fg-muted hover:text-fg'
            )}
          >
            {option.label}
            {typeof option.count === 'number' ? (
              <span
                className={cn(
                  'rounded-full px-1.5 py-px text-[0.6875rem] font-semibold tabular-nums',
                  active
                    ? 'bg-accent-soft text-brand-700 dark:text-brand-200'
                    : 'bg-raised text-fg-subtle'
                )}
              >
                {option.count}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}
